// SlideshowModal — full-screen album slideshow with Ken Burns pan/zoom on photos,
// 3-second slow-motion clips for videos, host-picked music (uploaded MP3 or none),
// and an "AI Music (coming soon)" placeholder for the premium ElevenLabs upgrade.
//
// Flow:
//   1. Setup stage — pick a music source (or none) and start.
//   2. Playing stage — full-screen slides, cross-fade between items, autoplay music,
//      keyboard shortcuts (← → space esc), tap controls on mobile.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import {
  X as XIcon, Play, Pause, ChevronLeft, ChevronRight, Music, Sparkles,
  Volume2, VolumeX, Upload, Film,
} from "lucide-react";
import { Media } from "../lib/api";

const PHOTO_MS = 3000;       // 3s per photo (spec)
const VIDEO_MS = 3000;       // 3s of slow-mo video (spec)
const VIDEO_RATE = 0.5;      // playbackRate — half-speed slow-mo

// Auto-suggested vibe copy for the "AI Music (coming soon)" placeholder
const AUTO_VIBES = {
  wedding:      "Cinematic romantic piano with soft strings, 70 BPM",
  birthday:     "Upbeat feel-good acoustic pop, 110 BPM, warm claps",
  party:        "Energetic electronic dance, 128 BPM, punchy drops",
  baby:         "Soft lullaby music-box with tender piano, 60 BPM",
  graduation:   "Uplifting cinematic orchestral swell, 90 BPM",
  anniversary:  "Warm romantic slow jazz with brush drums, 75 BPM",
  engagement:   "Sparkling romantic piano and light strings, 80 BPM",
  trip:         "Adventurous cinematic indie folk with claps, 100 BPM",
  housewarming: "Cozy acoustic guitar and light percussion, 90 BPM",
  other:        "Warm cinematic soundtrack, 90 BPM, hopeful mood",
};

export default function SlideshowModal({ open, onClose, album, media }) {
  const [stage, setStage] = useState("setup"); // "setup" | "playing"
  const [musicFile, setMusicFile] = useState(null);       // File | null
  const [musicUrl, setMusicUrl] = useState("");           // object URL
  const [aiPromptOpen, setAiPromptOpen] = useState(false);

  // Only photos + videos count; sort oldest → newest for a natural narrative
  const slides = useMemo(() => {
    if (!Array.isArray(media)) return [];
    return [...media]
      .filter((m) => !m.is_deleted)
      .sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
  }, [media]);

  // Cleanup object URLs
  useEffect(() => {
    return () => { if (musicUrl) URL.revokeObjectURL(musicUrl); };
  }, [musicUrl]);

  // Reset when reopened
  useEffect(() => {
    if (open) {
      setStage("setup");
    } else {
      setMusicFile(null);
      if (musicUrl) { URL.revokeObjectURL(musicUrl); setMusicUrl(""); }
      setAiPromptOpen(false);
    }
  }, [open, musicUrl]);

  const pickMusic = (file) => {
    if (!file) return;
    if (!file.type.startsWith("audio/")) {
      toast.error("Please pick an audio file (MP3, M4A, WAV…)");
      return;
    }
    if (musicUrl) URL.revokeObjectURL(musicUrl);
    setMusicFile(file);
    setMusicUrl(URL.createObjectURL(file));
  };

  if (!open) return null;

  const cfgVibe = AUTO_VIBES[album?.icon] || AUTO_VIBES.other;

  const setupUI = (
    <div className="w-full max-w-lg p-6 rounded-3xl" style={{
      background: "#15131F",
      border: "1px solid rgba(255,255,255,0.14)",
      boxShadow: "0 30px 80px -20px rgba(0,0,0,0.9)",
    }}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl flex items-center justify-center"
               style={{ background: "rgba(127,75,255,0.18)", border: "1px solid rgba(127,75,255,0.5)" }}>
            <Film size={20} color="#C9B6FF" />
          </div>
          <div>
            <div className="text-lg font-semibold">Play slideshow</div>
            <div className="text-xs" style={{ color: "rgba(255,255,255,0.55)" }}>
              {slides.length} {slides.length === 1 ? "item" : "items"} · about {Math.round(slides.length * 3)}s
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="w-9 h-9 rounded-full flex items-center justify-center"
          style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.14)" }}
          data-testid="slideshow-close-setup"
          aria-label="Close"
        >
          <XIcon size={16} />
        </button>
      </div>

      <div className="text-xs font-semibold tracking-wider mb-2" style={{ color: "rgba(255,255,255,0.65)" }}>
        MUSIC
      </div>

      {/* Upload your own */}
      <label
        className="flex items-center gap-3 rounded-2xl px-4 py-3 mb-2 cursor-pointer transition"
        style={{
          background: musicFile ? "rgba(34,226,255,0.10)" : "rgba(255,255,255,0.03)",
          border: `1px solid ${musicFile ? "rgba(34,226,255,0.45)" : "rgba(255,255,255,0.12)"}`,
        }}
        data-testid="slideshow-music-upload"
      >
        <div className="w-9 h-9 rounded-xl flex items-center justify-center"
             style={{ background: "rgba(34,226,255,0.14)", border: "1px solid rgba(34,226,255,0.4)" }}>
          <Upload size={16} color="#A8F2FF" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium">
            {musicFile ? musicFile.name : "Upload your own music"}
          </div>
          <div className="text-xs truncate" style={{ color: "rgba(255,255,255,0.55)" }}>
            {musicFile ? "Tap to change" : "MP3 / M4A / WAV — plays through the slideshow"}
          </div>
        </div>
        <input
          type="file"
          accept="audio/*"
          className="hidden"
          onChange={(e) => pickMusic(e.target.files?.[0])}
        />
      </label>

      {/* AI Music (coming soon) */}
      <button
        type="button"
        onClick={() => setAiPromptOpen((v) => !v)}
        className="w-full flex items-center gap-3 rounded-2xl px-4 py-3 mb-2 text-left transition"
        style={{
          background: "linear-gradient(135deg, rgba(255,122,0,0.10), rgba(255,31,107,0.10))",
          border: "1px solid rgba(255,122,0,0.35)",
        }}
        data-testid="slideshow-music-ai"
      >
        <div className="w-9 h-9 rounded-xl flex items-center justify-center"
             style={{ background: "rgba(255,122,0,0.16)", border: "1px solid rgba(255,122,0,0.4)" }}>
          <Sparkles size={16} color="#FFC08A" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium flex items-center gap-2">
            AI Music
            <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold"
                  style={{ background: "rgba(255,211,0,0.18)", color: "#FFD300", border: "1px solid rgba(255,211,0,0.45)" }}>
              PREMIUM · coming soon
            </span>
          </div>
          <div className="text-xs truncate" style={{ color: "rgba(255,255,255,0.6)" }}>
            Auto-generate a custom track for this event
          </div>
        </div>
      </button>
      {aiPromptOpen && (
        <div className="rounded-2xl px-4 py-3 mb-2 text-sm"
             style={{ background: "rgba(255,255,255,0.03)", border: "1px dashed rgba(255,255,255,0.14)", color: "rgba(255,255,255,0.75)" }}>
          <div className="text-xs uppercase tracking-wider mb-1" style={{ color: "rgba(255,255,255,0.5)" }}>
            Auto vibe suggestion
          </div>
          <div className="italic" style={{ color: "rgba(255,255,255,0.85)" }}>&ldquo;{cfgVibe}&rdquo;</div>
          <div className="mt-2 text-xs" style={{ color: "rgba(255,255,255,0.55)" }}>
            AI music generation via ElevenLabs is coming as a paid add-on (~$1.50 per 30s track).
            Until it launches, upload your own song above.
          </div>
        </div>
      )}

      {/* No music */}
      <button
        type="button"
        onClick={() => { setMusicFile(null); if (musicUrl) { URL.revokeObjectURL(musicUrl); setMusicUrl(""); } toast.success("Silent slideshow"); }}
        className="w-full flex items-center gap-3 rounded-2xl px-4 py-3 mb-4 text-left transition"
        style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.12)" }}
        data-testid="slideshow-music-none"
      >
        <div className="w-9 h-9 rounded-xl flex items-center justify-center"
             style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.14)" }}>
          <VolumeX size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium">No music</div>
          <div className="text-xs" style={{ color: "rgba(255,255,255,0.55)" }}>
            Silent slideshow — video audio still plays
          </div>
        </div>
      </button>

      <button
        type="button"
        onClick={() => {
          if (slides.length === 0) { toast.error("Album has no media yet"); return; }
          setStage("playing");
        }}
        className="w-full inline-flex items-center justify-center gap-2 rounded-full font-semibold text-base px-4 py-3"
        style={{
          background: "linear-gradient(90deg, #7F4BFF 0%, #4D1EFF 50%, #22E2FF 100%)",
          color: "#fff",
          boxShadow: "0 14px 36px -12px rgba(127,75,255,0.55)",
        }}
        data-testid="slideshow-start-btn"
      >
        <Play size={16} fill="#fff" /> Start slideshow
      </button>
    </div>
  );

  return createPortal(
    <div
      className="fixed inset-0"
      style={{ zIndex: 90, background: "rgba(6,4,14,0.88)", backdropFilter: "blur(14px)" }}
      role="dialog"
      aria-modal="true"
      data-testid="slideshow-portal"
    >
      {stage === "setup" && (
        <div className="w-full h-full flex items-center justify-center p-4">
          {setupUI}
        </div>
      )}
      {stage === "playing" && (
        <SlideshowPlayer
          slides={slides}
          musicUrl={musicUrl}
          onClose={onClose}
          onBack={() => setStage("setup")}
        />
      )}
    </div>,
    document.body,
  );
}

// ───────────────────────────────── Player ─────────────────────────────────

function SlideshowPlayer({ slides, musicUrl, onClose, onBack }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [muted, setMuted] = useState(false);
  const [progress, setProgress] = useState(0); // 0..1 within current slide
  const audioRef = useRef(null);
  const videoRef = useRef(null);
  const tickerRef = useRef(null);
  const slideStartRef = useRef(Date.now());
  const pausedElapsedRef = useRef(0);

  const current = slides[index];
  const total = slides.length;

  // Advance to next slide (or loop back to start)
  const goNext = useCallback(() => {
    setIndex((i) => (i + 1 >= total ? 0 : i + 1));
  }, [total]);

  const goPrev = useCallback(() => {
    setIndex((i) => (i - 1 < 0 ? total - 1 : i - 1));
  }, [total]);

  // Reset progress timer whenever slide changes
  useEffect(() => {
    slideStartRef.current = Date.now();
    pausedElapsedRef.current = 0;
    setProgress(0);
  }, [index]);

  // Progress ticker + auto-advance
  useEffect(() => {
    if (paused) return;
    const duration = current?.is_video ? VIDEO_MS : PHOTO_MS;
    tickerRef.current = setInterval(() => {
      const elapsed = Date.now() - slideStartRef.current + pausedElapsedRef.current;
      const pct = Math.min(1, elapsed / duration);
      setProgress(pct);
      if (pct >= 1) {
        clearInterval(tickerRef.current);
        goNext();
      }
    }, 60);
    return () => clearInterval(tickerRef.current);
  }, [index, paused, current, goNext]);

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") goNext();
      else if (e.key === "ArrowLeft") goPrev();
      else if (e.key === " ") { e.preventDefault(); setPaused((p) => !p); }
      else if (e.key === "m" || e.key === "M") setMuted((m) => !m);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goNext, goPrev, onClose]);

  // Music autoplay / pause / mute
  useEffect(() => {
    const a = audioRef.current;
    if (!a || !musicUrl) return;
    a.muted = muted;
    if (paused) a.pause();
    else a.play().catch(() => { /* browser may block until user interacts — first click handles it */ });
  }, [paused, muted, musicUrl]);

  // Video slow-mo + volume follow
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.playbackRate = VIDEO_RATE;
    v.muted = muted || Boolean(musicUrl); // if bg music is playing, mute video audio to avoid clashing
    if (paused) v.pause();
    else v.play().catch(() => {});
  }, [index, paused, muted, musicUrl]);

  const togglePause = () => {
    if (paused) {
      // resuming — shift start time so elapsed carries over
      slideStartRef.current = Date.now();
    } else {
      pausedElapsedRef.current += Date.now() - slideStartRef.current;
    }
    setPaused((p) => !p);
  };

  if (!current) {
    return (
      <div className="w-full h-full flex items-center justify-center text-white">
        <div className="text-center">
          <div className="mb-3">No media to play</div>
          <button onClick={onClose} className="meye-chip" style={{ padding: "8px 12px" }}>Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 flex flex-col" style={{ background: "#000" }}>
      {/* Hidden background audio */}
      {musicUrl && (
        <audio
          ref={audioRef}
          src={musicUrl}
          loop
          autoPlay
          data-testid="slideshow-audio"
        />
      )}

      {/* Slide stage */}
      <div className="flex-1 relative overflow-hidden" onClick={togglePause}>
        {current.is_video ? (
          <video
            ref={videoRef}
            key={current.id}
            src={Media.previewUrl(current.id)}
            className="absolute inset-0 w-full h-full object-contain"
            playsInline
            autoPlay
            muted={muted || Boolean(musicUrl)}
            data-testid="slideshow-video"
          />
        ) : (
          <img
            key={current.id}
            src={Media.previewUrl(current.id)}
            alt=""
            className="absolute inset-0 w-full h-full object-contain slideshow-kenburns"
            style={{ animationPlayState: paused ? "paused" : "running" }}
            data-testid="slideshow-photo"
          />
        )}

        {/* Slide counter + uploader chip */}
        <div className="absolute top-4 left-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs"
             style={{ background: "rgba(0,0,0,0.55)", color: "#fff", backdropFilter: "blur(8px)" }}>
          <span className="font-medium">{index + 1} / {total}</span>
          {current.uploader_name && (
            <>
              <span style={{ color: "rgba(255,255,255,0.5)" }}>·</span>
              <span>by {current.uploader_name}</span>
            </>
          )}
        </div>

        {/* Close */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          className="absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center"
          style={{ background: "rgba(0,0,0,0.55)", color: "#fff", backdropFilter: "blur(8px)", border: "1px solid rgba(255,255,255,0.14)" }}
          data-testid="slideshow-close"
          aria-label="Close slideshow"
        >
          <XIcon size={18} />
        </button>
      </div>

      {/* Bottom bar */}
      <div className="px-4 py-3" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.85), rgba(0,0,0,0))" }}>
        {/* Progress */}
        <div className="w-full h-1 rounded-full mb-3 overflow-hidden" style={{ background: "rgba(255,255,255,0.14)" }}>
          <div
            className="h-full rounded-full transition-[width]"
            style={{
              width: `${progress * 100}%`,
              background: "linear-gradient(90deg, #7F4BFF, #22E2FF)",
              transitionDuration: "60ms",
            }}
          />
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onBack(); }}
            className="text-sm px-3 py-1.5 rounded-full"
            style={{ background: "rgba(255,255,255,0.06)", color: "#fff", border: "1px solid rgba(255,255,255,0.14)" }}
            data-testid="slideshow-back"
          >
            Music
          </button>

          <div className="flex-1" />

          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); goPrev(); }}
            className="w-10 h-10 rounded-full flex items-center justify-center"
            style={{ background: "rgba(255,255,255,0.06)", color: "#fff", border: "1px solid rgba(255,255,255,0.14)" }}
            data-testid="slideshow-prev"
            aria-label="Previous"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); togglePause(); }}
            className="w-12 h-12 rounded-full flex items-center justify-center"
            style={{
              background: "linear-gradient(135deg, #7F4BFF, #22E2FF)",
              color: "#fff",
              boxShadow: "0 8px 24px -6px rgba(127,75,255,0.55)",
            }}
            data-testid="slideshow-playpause"
            aria-label={paused ? "Play" : "Pause"}
          >
            {paused ? <Play size={18} fill="#fff" /> : <Pause size={18} fill="#fff" />}
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); goNext(); }}
            className="w-10 h-10 rounded-full flex items-center justify-center"
            style={{ background: "rgba(255,255,255,0.06)", color: "#fff", border: "1px solid rgba(255,255,255,0.14)" }}
            data-testid="slideshow-next"
            aria-label="Next"
          >
            <ChevronRight size={18} />
          </button>

          <div className="flex-1" />

          {musicUrl && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setMuted((m) => !m); }}
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ background: "rgba(255,255,255,0.06)", color: "#fff", border: "1px solid rgba(255,255,255,0.14)" }}
              data-testid="slideshow-mute"
              aria-label={muted ? "Unmute" : "Mute"}
            >
              {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
